import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { SnakeCasedPropertiesDeep } from 'type-fest'
import { getInstance } from '@/auth'
import {
  convertToCamelCase,
  convertToSnakeCase,
  SkipKey,
} from '@/models/utils/casing'

export const BaseUrl = '/api'
export const DefaultApiVersion = '2024-06-01'

type DataType = Record<string, unknown> | unknown[] | null | undefined

interface ApiCallArgs<T = Record<string, unknown>> {
  data?: T
  config?: AxiosRequestConfig
  cancelToken?: unknown

  [key: string]: unknown
}

interface CamelCaseOptions {
  skipKeys?: SkipKey[]
}

const addAuthRequestInterceptor = (instance: AxiosInstance): AxiosInstance => {
  instance.interceptors.request.use(
    async function (
      config: InternalAxiosRequestConfig
    ): Promise<InternalAxiosRequestConfig> {
      const authService = getInstance()
      const token = await authService.getTokenSilently()
      if (config.url?.startsWith(BaseUrl)) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    function (error: unknown): Promise<never> {
      return Promise.reject(error)
    }
  )

  return instance
}

export function addCamelCaseResponseInterceptor(
  instance: AxiosInstance,
  options: CamelCaseOptions = {}
): AxiosInstance {
  const { skipKeys = [] } = options

  instance.interceptors.response.use(
    (response: AxiosResponse): AxiosResponse => {
      if (
        response.data &&
        (typeof response.data === 'object' || Array.isArray(response.data))
      ) {
        response.data = convertToCamelCase(response.data, skipKeys) as DataType
      }
      return response
    },
    (error: { response?: { data?: DataType } }): Promise<never> => {
      if (error.response && error.response.data) {
        error.response.data = convertToCamelCase(
          error.response.data,
          skipKeys
        ) as typeof error.response.data
      }
      return Promise.reject(error)
    }
  )

  return instance
}

export function addSnakeCaseRequestInterceptor(
  instance: AxiosInstance
): AxiosInstance {
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      if (
        config.data &&
        (typeof config.data === 'object' || Array.isArray(config.data))
      ) {
        config.data = convertToSnakeCase(config.data as DataType)
      }
      return config
    },
    (error: unknown): Promise<never> => {
      return Promise.reject(error)
    }
  )

  return instance
}

export function createClient(version: string): AxiosInstance {
  const skipKeys = [
    { key: 'metadata', onlyDeep: true },
    { key: 'custom_data', onlyDeep: true },
  ]

  let instance = axios.create()
  instance = addAuthRequestInterceptor(instance)
  instance = addSnakeCaseRequestInterceptor(instance)
  instance = addCamelCaseResponseInterceptor(instance, { skipKeys })

  instance.defaults.headers.common['Api-Version'] = version

  return instance
}

export const axiosStrict = createClient(DefaultApiVersion)

export function get<T = Record<string, unknown>>(
  url: string,
  args: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> {
  return axiosStrict.get<SnakeCasedPropertiesDeep<T>, AxiosResponse<T>>(
    url,
    args
  )
}

export function patch<T = Record<string, unknown>, D = Record<string, unknown>>(
  url: string,
  args: ApiCallArgs<D> = {}
): Promise<AxiosResponse<T>> {
  return axiosStrict.patch<SnakeCasedPropertiesDeep<T>, AxiosResponse<T>>(
    url,
    args.data || {},
    args.config
  )
}

export function post<T = Record<string, unknown>, D = Record<string, unknown>>(
  url: string,
  args: ApiCallArgs<D> = {}
): Promise<AxiosResponse<T>> {
  return axiosStrict.post<SnakeCasedPropertiesDeep<T>, AxiosResponse<T>>(
    url,
    args.data || {},
    args.config
  )
}

export function put<T = Record<string, unknown>, D = Record<string, unknown>>(
  url: string,
  args: ApiCallArgs<D> = {}
): Promise<AxiosResponse<T>> {
  return axiosStrict.put<SnakeCasedPropertiesDeep<T>, AxiosResponse<T>>(
    url,
    args.data || {},
    args.config
  )
}

export function del<T = Record<string, unknown>>(
  url: string,
  args: AxiosRequestConfig = {}
): Promise<AxiosResponse<T>> {
  return axiosStrict.delete<SnakeCasedPropertiesDeep<T>, AxiosResponse<T>>(
    url,
    args
  )
}
